/**
 * useSDKLauncher Hook
 * Orchestrates Prizeout SDK initialization and lifecycle management
 */

import { useCallback, useEffect, useRef } from 'react';

import {
    getSDKSettings,
    logout,
    selectIsWidgetLoaded,
    selectSDKError,
    selectSDKLaunching,
    setSDKError,
    setSDKLaunching,
    setWidgetLoaded,
    updateBalance,
} from '../store/slices';
import { removeSessionCookie } from '../utils/cookies';
import { disableSDKFullscreen, enableSDKFullscreen } from '../utils/sdk-body-state';
import {
    buildSDKConfig,
    CashoutPayload,
    getSDKEnvironmentConfig,
    isSDKAvailable,
    isSDKInitialized,
    setSDKInitializedFlag,
} from '../utils/sdk-config';
import { useAppDispatch } from './use-app-dispatch';
import { useAppSelector } from './use-app-selector';

export interface UseSDKLauncherReturn {
    launchSDK: () => Promise<void>;
    isWidgetLoaded: boolean;
    closeSDK: () => void;
    error: string | null;
    isLoading: boolean;
}

type SDKStatus = 'idle' | 'initializing' | 'initialized';

export const useSDKLauncher = (): UseSDKLauncherReturn => {
    const dispatch = useAppDispatch();
    const sdkStatus = useRef<SDKStatus>('idle');
    const isWidgetLoaded = useAppSelector(selectIsWidgetLoaded);
    // Use Redux state for loading/error so ALL hook instances share the same state
    const isLoading = useAppSelector(selectSDKLaunching);
    const error = useAppSelector(selectSDKError);

    /**
     * Reset SDK state to idle
     */
    const resetSDKState = useCallback(() => {
        sdkStatus.current = 'idle';
        setSDKInitializedFlag(false);
        dispatch(setSDKLaunching(false));
    }, [dispatch]);

    /**
     * Handle SDK initialization success
     */
    const handleSDKInit = useCallback(() => {
        enableSDKFullscreen();
        dispatch(setWidgetLoaded(true));
        dispatch(setSDKLaunching(false));
    }, [dispatch]);

    /**
     * Handle SDK close event
     */
    const handleSDKClose = useCallback(() => {
        disableSDKFullscreen();
        removeSessionCookie();
        dispatch(logout());
        resetSDKState();
        window.location.reload();
    }, [dispatch, resetSDKState]);

    /**
     * Handle cashout success
     */
    const handleCashoutSuccess = useCallback(
        (payload: CashoutPayload) => {
            const amountDeducted = (payload.giftcard.cost_in_cents || 0) / 100;
            dispatch(updateBalance(-amountDeducted));
        },
        [dispatch],
    );

    /**
     * Launch the Prizeout SDK widget
     */
    const launchSDK = useCallback(async (): Promise<void> => {
        // Prevent multiple initializations using both local and global state
        if (sdkStatus.current !== 'idle' || isSDKInitialized()) {
            return;
        }

        if (!isSDKAvailable()) {
            dispatch(setSDKError('Prizeout SDK not loaded. Please refresh the page.'));
            return;
        }

        // Mark as initializing immediately to prevent race conditions
        // Use Redux dispatch so ALL hook instances see the loading state
        sdkStatus.current = 'initializing';
        dispatch(setSDKLaunching(true));
        dispatch(setSDKError(null));

        try {
            const envConfig = getSDKEnvironmentConfig();

            const settings = await dispatch(getSDKSettings()).unwrap();

            // Double-check we haven't been initialized while waiting
            if (isSDKInitialized()) {
                sdkStatus.current = 'idle';
                dispatch(setSDKLaunching(false));
                return;
            }

            const sdkConfig = buildSDKConfig(settings, envConfig);

            // Mark as initialized BEFORE calling init to prevent race conditions
            sdkStatus.current = 'initialized';
            setSDKInitializedFlag(true);

            // Prepare body for fullscreen SDK
            enableSDKFullscreen();

            window.prizeoutSDK!.init({
                ...sdkConfig,
                callbacks: {
                    onInit: handleSDKInit,
                    onClose: handleSDKClose,
                    onCashoutFail: () => {
                        // Handle cashout failure if needed
                    },
                    onCashoutSuccess: handleCashoutSuccess,
                },
            });
        } catch (err) {
            const errorMessage = typeof err === 'string' ? err : 'Failed to load SDK settings. Please try again.';
            dispatch(setSDKError(errorMessage));
            resetSDKState();
            dispatch(setWidgetLoaded(false));
        }
    }, [dispatch, handleSDKInit, handleSDKClose, handleCashoutSuccess, resetSDKState]);

    /**
     * Close the SDK widget
     */
    const closeSDK = useCallback(() => {
        if (window.prizeoutSDK?.close) {
            window.prizeoutSDK.close();
        }
        disableSDKFullscreen();
        dispatch(setWidgetLoaded(false));
        resetSDKState();
    }, [dispatch, resetSDKState]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (sdkStatus.current === 'initialized') {
                disableSDKFullscreen();
            }
        };
    }, []);

    return {
        launchSDK,
        isWidgetLoaded,
        closeSDK,
        error,
        isLoading,
    };
};

export default useSDKLauncher;
